import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useToast } from '../../../contexts/ToastContext';
import authService from '../../../utils/authService';

const SecuritySection = () => {
  const { showSuccess, showError } = useToast();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  const activeSessions = [
    {
      id: 1,
      device: "MacBook Pro",
      browser: "Chrome 120.0",
      location: "New York, NY",
      ipAddress: "192.168.1.100",
      lastActive: "2 minutes ago",
      isCurrent: true
    },
    {
      id: 2,
      device: "iPhone 15",
      browser: "Safari Mobile",
      location: "New York, NY",
      ipAddress: "192.168.1.101",
      lastActive: "1 hour ago",
      isCurrent: false
    },
    {
      id: 3,
      device: "Windows PC",
      browser: "Edge 119.0",
      location: "Boston, MA",
      ipAddress: "10.0.0.50",
      lastActive: "3 days ago",
      isCurrent: false
    }
  ];

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('At least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Include uppercase letters');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Include lowercase letters');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Include at least one number');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Include at least one special character');
    }
    
    return errors;
  };

  const handlePasswordChange = async () => {
    // Clear previous errors
    setPasswordErrors({});
    
    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors({ confirmPassword: 'Passwords do not match' });
      showError('New passwords do not match');
      return;
    }
    
    // Validate new password strength
    const passwordValidationErrors = validatePassword(passwordData.newPassword);
    if (passwordValidationErrors.length > 0) {
      setPasswordErrors({ newPassword: passwordValidationErrors.join(', ') });
      showError('Password does not meet requirements');
      return;
    }
    
    // Check if new password is different from current
    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordErrors({ newPassword: 'New password must be different from current password' });
      showError('New password must be different from current password');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      const result = await authService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      
      if (result.success) {
        showSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordErrors({});
      } else {
        showError(result.error || 'Failed to change password');
        if (result.error.includes('current') || result.error.includes('old')) {
          setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        }
      }
    } catch (error) {
      showError('An unexpected error occurred. Please try again.');
      console.error('Password change error:', error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSessionTerminate = (sessionId) => {
    if (window.confirm('Are you sure you want to terminate this session?')) {
      // Simulate session termination
      console.log(`Terminating session ${sessionId}`);
    }
  };

  const getDeviceIcon = (device) => {
    if (device.includes('iPhone') || device.includes('Android')) return 'Smartphone';
    if (device.includes('MacBook') || device.includes('Windows')) return 'Laptop';
    return 'Monitor';
  };

  return (
    <div className="space-y-6">
      {/* Security Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Security Settings</h2>
        <p className="text-sm text-text-secondary mt-1">
          Manage your account security and authentication preferences
        </p>
      </div>

      {/* Password Change */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Change Password</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Current Password
            </label>
            <div className="relative">
              <Input
                type={showPasswords.current ? "text" : "password"}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  currentPassword: e.target.value
                }))}
                placeholder="Enter current password"
                className={`pr-10 ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <Icon 
                  name={showPasswords.current ? "EyeOff" : "Eye"} 
                  size={16} 
                  className="text-text-secondary hover:text-text-primary"
                />
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                type={showPasswords.new ? "text" : "password"}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  newPassword: e.target.value
                }))}
                placeholder="Enter new password"
                className={`pr-10 ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <Icon 
                  name={showPasswords.new ? "EyeOff" : "Eye"} 
                  size={16} 
                  className="text-text-secondary hover:text-text-primary"
                />
              </button>
            </div>
            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? "text" : "password"}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  confirmPassword: e.target.value
                }))}
                placeholder="Confirm new password"
                className={`pr-10 ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <Icon 
                  name={showPasswords.confirm ? "EyeOff" : "Eye"} 
                  size={16} 
                  className="text-text-secondary hover:text-text-primary"
                />
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
            )}
          </div>
          
          <Button
            variant="primary"
            onClick={handlePasswordChange}
            loading={isChangingPassword}
            disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            iconName="Lock"
            iconPosition="left"
            animation="glow"
          >
            Update Password
          </Button>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Password Requirements:</p>
              <ul className="mt-1 list-disc list-inside space-y-1 text-xs">
                <li>At least 8 characters long</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Include at least one number</li>
                <li>Include at least one special character</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication - Hidden */}
      {/* <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-text-primary">Two-Factor Authentication</h3>
            <p className="text-sm text-text-secondary mt-1">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${twoFactorEnabled ? 'text-success' : 'text-text-secondary'}`}>
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                twoFactorEnabled ? 'bg-success' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        
        {twoFactorEnabled && (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-md">
              <Icon name="Shield" size={20} className="text-success" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Two-factor authentication is active
                </p>
                <p className="text-xs text-green-600">
                  Your account is protected with SMS verification
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              iconPosition="left"
            >
              Configure 2FA Settings
            </Button>
          </div>
        )}
      </div> */}

      {/* Active Sessions - Hidden */}
      {/* <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-text-primary">Active Sessions</h3>
            <p className="text-sm text-text-secondary mt-1">
              Manage your active sessions across different devices
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
        
        <div className="space-y-3">
          {activeSessions.map((session) => (
            <div
              key={session.id}
              className="flex items-center justify-between p-4 border border-border rounded-md hover:bg-gray-50"
            >
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-md">
                  <Icon name={getDeviceIcon(session.device)} size={20} className="text-text-secondary" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-text-primary">
                      {session.device}
                    </h4>
                    {session.isCurrent && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary">
                    {session.browser} • {session.location}
                  </p>
                  <p className="text-xs text-text-secondary">
                    IP: {session.ipAddress} • Last active: {session.lastActive}
                  </p>
                </div>
              </div>
              
              {!session.isCurrent && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSessionTerminate(session.id)}
                  iconName="X"
                  iconPosition="left"
                  className="text-error hover:text-error"
                >
                  Terminate
                </Button>
              )}
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default SecuritySection;